import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslationContext } from "../../../../../ui/framework/hooks/TranslationContext";
import { useUserServiceContext } from "../../../../../ui/framework/hooks/UserServiceContext";
import { UserDialogTranslationsDictionary } from "../helpers/UserDialogTranslation";
import { IconArrowLeft, IconDriver, IconLogout, IconSort2Horizontal, List, ListItem, MobileSheet, Modal, Pill } from "@geotab/zenith";
import { useDutyStatusLogServiceContext } from "../../../../../ui/framework/hooks/DutyStatusLogServiceContext";
import { DisplayDetailEnum, getHosPillType, moveDriverToFirst } from "../helpers/UserDialogHelper";
import { isInCanada, THosStatus } from "hos-tools";
import { useStoreContext } from "../../../../../ui/framework/hooks/StoreContext";
import { useRouterContext } from "../../../../../ui/framework/hooks/RouterContext";
import User from "../../../../../model/User";
import { isNoVehicle } from "../helpers/ConnectionHelper";
import { useSecurityServiceContext } from "../../../../../ui/framework/hooks/SecurityServiceContext";
import { useFeatureFlagContext } from "../../../../../ui/framework/hooks/FeatureFlagContext";
import { FeatureFlagName } from "../../../../../utils/featureFlagName";
import { Event as EventType, event as loggerEvent } from "../../../../../utils/logger";
import { useAppContext } from "../../../../../ui/framework/hooks/AppViewContext";
import { sanitizeUserName } from "../../../../../utils/users";
import constants from "../../../../../constants";
import { getPageState, isWorkflow, toWorkflow } from "../helpers/Helper";
import { useHosUtilsContext } from "../../../../../ui/framework/hooks/HosUtilsContext";
import { LoginRequiredStatus } from "../../../../../store/mobile/types";
import rison from "rison";
import { clearCurrentVehicle, getCurrentVehicle } from "../../../../../utils/device";
import { handleApi } from "../../../../../utils/error";
import { dispatcher, DispatcherEvents } from "../../../../../utils/dispatcher";

const UserDialogContent: FC<{userMobileSheet: boolean; setUserMobileSheet: React.Dispatch<React.SetStateAction<boolean>>}> =
    ({userMobileSheet, setUserMobileSheet }) => {

    const { translate } = useTranslationContext();
    const { userService } = useUserServiceContext();
    const { dutyStatusLogService } = useDutyStatusLogServiceContext();
    const { store } = useStoreContext();
    const { router } = useRouterContext();
    const { hosUtils } = useHosUtilsContext();
    const { securityService } = useSecurityServiceContext();
    const { featureFlagsService } = useFeatureFlagContext();
    const { app } = useAppContext();

    const ref = useRef(null);

    const [users, setUsers] = useState<DeepReadonlyObject<IUser>[]>([]);
    const [lastUsersStatus, setUsersStatus] = useState({});
    const [maxUser, setMaxUser] = useState(0);
    const [displayDetail, setDisplayDetail] = useState<DisplayDetailEnum>(DisplayDetailEnum.default);
    const [currentDriver, setCurrentDriver] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState("");

    const getUserFullname = (user: DeepReadonlyObject<IUser> | null) => {
        return user ? `${ user.firstName } ${ user.lastName }` : "";
    };

    const getStatus = (id: string) => {
        let status = lastUsersStatus[id] as THosStatus;
        if (!status) {
            return null;
        }

        return (
            <Pill type={getHosPillType(status)}>
                { status }
            </Pill>
        );
    };

    const cleanUpModal = () => {
        setIsModalOpen(false);
        setModalContent("");
        router.navigate(`workflow,${ rison.encode({
            logout: false,
            step: "selectVehicle",
            changeVehicle: true
        }) }`, true);
    };

    const getLogoutText = (name: string) => {
        const logoutText = userService.getActiveUserNames().length > 1 ? ` (${ name })` : "";
        return translate(UserDialogTranslationsDictionary.logout) + logoutText;
    };

    const logout = () => {
        setUserMobileSheet(false);
        toWorkflow(router, true);
    };

    const switchDriver = (user: DeepReadonlyObject<IUser>) => {
        let userId = user.id;
        if (userId && userId.length > 0) {
            setUserMobileSheet(false);
            setDisplayDetail(DisplayDetailEnum.default);
            router.navigate(`login,(switchUser:${ userId })`, true);
        }
    };

    const handleAddDriver = () => {
        if (!isInCanada(hosUtils.getLatestHosRuleSetIdShortened()) || !getPageState(store).isLocked) {
            setUserMobileSheet(false);
            router.navigate("login,(addCoDriver:true)", true);
        }
    };

    const handleDriverSeat = async (userName: string) => {
        let userContext = app.manager.getCachedContext(userName);
        try {
            let hasAccess = await app.vehicleService.hasAccessToDevice(getCurrentVehicle() as IIdEntity, userContext ? userContext.api : undefined);
            if (hasAccess) {
                userService.setDriversSeatUser(userName);
                setCurrentDriver(sanitizeUserName(userName));
            } else {
                clearCurrentVehicle();
                setModalContent(translate(UserDialogTranslationsDictionary.noAccessToVehicle));
                setIsModalOpen(true);
            }

            setUserMobileSheet(false);
        } catch (err) {
            if (err.data.type === "NetworkError") {
                userService.setDriversSeatUser(userName);
                setCurrentDriver(sanitizeUserName(userName));
            } else {
                handleApi(err);
            }
        }
    };

    const isNotUsingVehicle = () => {
        if (store.getState().data.driving) {
            return false;
        }

        let userListStatus = Object.keys(lastUsersStatus);
        let statuses = ["D", "PC", "PC_Exempted", "WT", "YM"];

        return userListStatus.every(user => statuses.indexOf(lastUsersStatus[user]) === -1);
    };

    const isAddingCoDriver = () => {
        return store.getState().mobile.loginRequired.status === LoginRequiredStatus.AddCoDriver;
    };

    const canSwitchSeat = () => {
        return userService.getActiveUserNames().length > 1 && isNotUsingVehicle() && !isAddingCoDriver();
    };

    const dispatchFeedBackEvent = () => {
        setUserMobileSheet(false);

        // Open feedback dialog
        dispatcher.trigger(DispatcherEvents.ProvideFeedback);
    };

    useEffect(() => {
        if (!userMobileSheet) {
            setDisplayDetail(DisplayDetailEnum.default);
            return;
        }

        let lastStatuses = {};
        let activeUser = new User(userService.getCurrentUser());
        let usingNoVehicle = isNoVehicle(store);
        let canAccessHos = securityService.can("useHos") || (securityService.can("attemptHos") && usingNoVehicle);

         if (!activeUser || !activeUser.id) {
            // Panic! (Or just try logging in again)
            loggerEvent(EventType.DEBUG_LOGINVIEW_NAVIGATE, "openUserMenu: No active user found, redirecting to login.");
            router.navigate("login,(loggingIn:!t)", true);
            return;
        }

        app.network.checkBridge();
        setMaxUser(featureFlagsService.hasFeatureFlag(FeatureFlagName.MAX_DRIVERS_LIMIT_INCREASE) ?
                constants.SESSION.MAX_LOGGED_IN_USERS_OVERRIDE :
                constants.SESSION.MAX_LOGGED_IN_USERS);
        let activeDriver = userService.getDriversSeatUser();
        let activeDriverUsername = activeDriver ? sanitizeUserName(activeDriver.name) : "";

        let orderUsers = moveDriverToFirst(userService.getSortedUsers(true), sanitizeUserName(activeUser.name));
        setCurrentDriver(activeDriverUsername);
        setUsers(orderUsers);

        if (canAccessHos) {
            orderUsers.forEach((user) => {
                let tempDutyStatusLogs = dutyStatusLogService.getSortedHosLogs(dutyStatusLogService.getHOSLogs(false, user.id).filter(hosLog => hosLog.isDashboardLog()));
                let tempLastLog = dutyStatusLogService.getLatestLog(tempDutyStatusLogs);
                lastStatuses[user.id] = tempLastLog && tempLastLog.status ? tempLastLog.status : "OFF";
            });
        }
        setUsersStatus(lastStatuses);
    }, [userMobileSheet]);

    const renderCurrentUserAction = (item: DeepReadonlyObject<IUser>) => {
        if (isWorkflow(store)) {
            return undefined;
        }

        return (
            <ListItem image={<IconLogout size="huge" className="IconPrimary"/>} key={item.id + "_action"} id={"topbar-user-logout"} name={getLogoutText(getUserFullname(item))} onClick={logout} />
        );
    };

    const renderOtherUserAction = (item: DeepReadonlyObject<IUser>) => {
        if (isWorkflow(store)) {
            return undefined;
        }

        return (
            <ListItem image={<IconSort2Horizontal size="huge" className="IconPrimary"/>} key={item.id + "_action"} data-id={item.id} id={`topbar-switch-user-action-${ item.id }`} name={UserDialogTranslationsDictionary.switchTo} onClick={() => switchDriver(item)} />
        );
    };

    const renderActionButton = () => {
        if (isWorkflow(store)) {
            return undefined;
        }

        if (userService.getActiveUserNames().length >= maxUser && !canSwitchSeat()) {
            return undefined;
        }

        return (
            <>
                { userService.getActiveUserNames().length < maxUser ? <ListItem key="addDriver" id="topbar-user-add" name={translate(UserDialogTranslationsDictionary.addDriver)} onClick={() => handleAddDriver()} /> : undefined }
                { canSwitchSeat() ? <ListItem key="driveSeat" id="topbar-user-switch-primary" name={translate(UserDialogTranslationsDictionary.driverSeat)} secondary={getUserFullname(userService.getDriversSeatUser())} onClick={() => setDisplayDetail(DisplayDetailEnum.ChangeDriver)} /> : undefined }
            </>
        );
    };

    const renderFeedbackButton = () => {
        if (isWorkflow(store) || !constants.FEEDBACK.EXTERNAL_SUPPORT_URL) {
            return undefined;
        }

        return (
            <ListItem key="sendFeedback" id="topbar-user-feedback" name={translate(UserDialogTranslationsDictionary.sendFeedback)} onClick={dispatchFeedBackEvent} />
        );
    };

    const renderBackIcon = useMemo(() => {
        if (displayDetail === DisplayDetailEnum.default) {
            return undefined;
        }
        return (
            <div onClick={() => setDisplayDetail(DisplayDetailEnum.default)} style={{ cursor: "pointer" }}>
                <IconArrowLeft size="large" />
            </div>
        );
    }, [displayDetail]);

    const renderDefault = () => {
        return users && users.map((item, index) => {
            return (
                <React.Fragment key={item.id}>
                    <ListItem
                        className="user-dialog-list-item-user"
                        key={item.id + index}
                        id={item.id}
                        name={getUserFullname(item)}
                        secondary={index === 0 ? translate(UserDialogTranslationsDictionary.currentUser) : undefined}
                        status={getStatus(item.id)}
                    />
                    { index === 0 ? renderCurrentUserAction(item) : renderOtherUserAction(item) }
                    { index === users.length - 1 ? renderActionButton() : undefined }
                    { index === users.length - 1 ? renderFeedbackButton() : undefined }
                </React.Fragment>
            );
        });
    };

    const renderChangeDriver = () => {
        return (
            <>
                { users.map((item, index) => {
                    return (
                        <ListItem
                            key={item.id + index}
                            id={item.id}
                            data-id={item.id}
                            name={getUserFullname(item)}
                            secondary={currentDriver === sanitizeUserName(item.name) ? translate(UserDialogTranslationsDictionary.currentDriver) : undefined}
                            status={currentDriver === sanitizeUserName(item.name) ? <div style={{ paddingRight: "8px"}}><IconDriver size="huge" className="IconPrimary" /></div> : getStatus(item.id)}
                            onClick={currentDriver !== sanitizeUserName(item.name) ? async () => { await handleDriverSeat(item.name); } : undefined}
                        />
                    );
                }) }
            </>
        );
    };

    const render = () => {
        switch (displayDetail) {
            case DisplayDetailEnum.ChangeDriver:
                return renderChangeDriver();
            default:
                return renderDefault();
        }
    };

    return (
        <MobileSheet
            label={translate(UserDialogTranslationsDictionary.user)}
            triggerRef={ref}
            isOpen={userMobileSheet}
            onHidePanel={() => setUserMobileSheet(false)}
            onCloseClick={() => setUserMobileSheet(false)}>
            <MobileSheet.Title>
                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {renderBackIcon}
                    {translate(displayDetail)}
                </span>
            </MobileSheet.Title>
            <MobileSheet.Content>
                <List>
                    { render() }
                </List>
            </MobileSheet.Content>
            <Modal isOpen={isModalOpen} onClose={cleanUpModal} title={translate(UserDialogTranslationsDictionary.noAccessToVehicle)} >
                <div style={{
                    height: 150,
                    overflowY: "auto"
                }}>
                    { modalContent }
                </div>
                <Modal.TertiaryButton type="tertiary" onClick={cleanUpModal}>Tertiary</Modal.TertiaryButton>
            </Modal>
        </MobileSheet>
    );

};

export default UserDialogContent;